import { useState, useCallback, useMemo } from 'react';
import {
  IconButton,
  Paper,
  Toolbar,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider,
  Collapse,
  Chip,
  FormControlLabel,
  Switch,
  Slider,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import PlaceIcon from '@mui/icons-material/Place';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RoomIcon from '@mui/icons-material/Room';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import GradientIcon from '@mui/icons-material/Gradient';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import MapView from '../map/core/MapView';
import MapEventMarkers, { EVENT_ICONS } from '../map/MapEventMarkers';
import MapHeatLayer, { HEATMAP_COLOR_SCHEMES } from '../map/MapHeatLayer';
import { formatTime } from '../common/util/formatter';
import ReportFilter from '../reports/components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch } from '../reactHelper';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import StatusCard from '../common/components/StatusCard';
import MapScale from '../map/MapScale';
import BackIcon from '../common/components/BackIcon';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { TOPBAR_HEIGHT } from '../common/components/TopBar';
import { GAP } from '../common/theme/layout';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { prefixString } from '../common/util/stringUtils';

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
  },
  mapContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    zIndex: 3,
    left: 0,
    top: TOPBAR_HEIGHT + GAP.SMALL,
    margin: theme.spacing(1.5),
    width: theme.dimensions.drawerWidthDesktop,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      margin: 0,
      top: TOPBAR_HEIGHT + GAP.SMALL,
    },
  },
  title: {
    flexGrow: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0.5),
    },
  },
  section: {
    marginBottom: theme.spacing(1),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(0.5, 0),
  },
  filterButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  stats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  legend: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  viewModeToggle: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    '& .MuiSlider-root': {
      flex: 1,
    },
  },
  sliderLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    minWidth: 60,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  sliderValue: {
    fontSize: '0.7rem',
    color: theme.palette.text.primary,
    minWidth: 40,
    textAlign: 'right',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

/**
 * Configuração dos esquemas de cores para heatmap
 */
const COLOR_SCHEME_CONFIG = {
  classic: { label: 'Clássico', icon: <GradientIcon fontSize="small" />, color: '#4CAF50' },
  fire: { label: 'Fogo', icon: <WhatshotIcon fontSize="small" />, color: '#FF5722' },
  cool: { label: 'Frio', icon: <BlurOnIcon fontSize="small" />, color: '#2196F3' },
  viridis: { label: 'Viridis', icon: <BlurOnIcon fontSize="small" />, color: '#9C27B0' },
  density: { label: 'Densidade', icon: <BlurOnIcon fontSize="small" />, color: '#E91E63' },
};

/**
 * Tipos de eventos para filtro
 */
const EVENT_FILTERS = [
  { type: 'all', label: 'Todos', icon: <NotificationsActiveIcon fontSize="small" /> },
  { type: 'alarm', label: 'Alarmes', icon: <WarningAmberIcon fontSize="small" />, color: '#FF5722' },
  { type: 'geofenceEnter', label: 'Entradas', icon: <LoginIcon fontSize="small" />, color: '#4CAF50' },
  { type: 'geofenceExit', label: 'Saídas', icon: <LogoutIcon fontSize="small" />, color: '#F44336' },
  { type: 'deviceOverspeed', label: 'Velocidade', icon: <SpeedIcon fontSize="small" />, color: '#FF9800' },
  { type: 'ignition', label: 'Ignição', icon: <PowerSettingsNewIcon fontSize="small" />, color: '#9E9E9E' },
];

/**
 * EventMapPage - Mapa de Eventos standalone
 *
 * Similar ao HeatmapPage, mas mostra eventos como marcadores no mapa.
 * Baseado no PHP: content_mapa_evento.php
 */
const EventMapPage = () => {
  const t = useTranslation();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  const [searchParams] = useSearchParams();
  const defaultDeviceId = useSelector((state) => state.devices.selectedId);
  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const geofences = useSelector((state) => state.geofences.items, shallowEqual);

  // Estado dos dados
  const [events, setEvents] = useState([]);
  const [positions, setPositions] = useState({});
  const [selectedDeviceId, setSelectedDeviceId] = useState(defaultDeviceId);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [loading, setLoading] = useState(false);

  // Controles do mapa
  const [viewMode, setViewMode] = useState('markers'); // 'markers' ou 'heatmap'
  const [filterType, setFilterType] = useState('all');
  const [showGeofences, setShowGeofences] = useState(true);
  const [settingsExpanded, setSettingsExpanded] = useState(true);

  // Controles do heatmap
  const [radius, setRadius] = useState(25);
  const [intensity, setIntensity] = useState(1);
  const [opacity, setOpacity] = useState(0.8);
  const [colorScheme, setColorScheme] = useState('fire');

  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const loaded = Boolean(from && to && !loading && events.length);

  // Nome do dispositivo
  const deviceName = useSelector((state) => {
    if (selectedDeviceId) {
      const device = state.devices.items[selectedDeviceId];
      if (device) {
        return device.name;
      }
    }
    return null;
  });

  // Eventos filtrados
  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events;
    if (filterType === 'ignition') {
      return events.filter((e) => e.type === 'ignitionOn' || e.type === 'ignitionOff');
    }
    return events.filter((e) => e.type === filterType);
  }, [events, filterType]);

  // Estatísticas
  const stats = useMemo(() => {
    if (!events.length) return null;

    const byType = {};
    events.forEach((event) => {
      const type = event.type || 'unknown';
      byType[type] = (byType[type] || 0) + 1;
    });

    return {
      total: events.length,
      alarms: byType.alarm || 0,
      geofenceEnter: byType.geofenceEnter || 0,
      geofenceExit: byType.geofenceExit || 0,
      overspeed: byType.deviceOverspeed || 0,
      ignitionOn: byType.ignitionOn || 0,
      ignitionOff: byType.ignitionOff || 0,
    };
  }, [events]);

  // Posições para centralizar mapa
  const mapPositions = useMemo(() => {
    return filteredEvents
      .map((event) => positions[event.positionId])
      .filter((pos) => pos && pos.latitude && pos.longitude);
  }, [filteredEvents, positions]);

  // Posições para heatmap (converte eventos em array de posições)
  const heatmapPositions = useMemo(() => {
    return filteredEvents
      .map((event) => {
        const pos = positions[event.positionId];
        if (pos && pos.latitude && pos.longitude) {
          return {
            ...pos,
            eventType: event.type,
            eventId: event.id,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [filteredEvents, positions]);

  // Handler para buscar dados
  const handleShow = useCatch(async ({ deviceIds, from, to }) => {
    const deviceId = deviceIds.find(() => true);
    setLoading(true);
    setSelectedDeviceId(deviceId);
    setSelectedEvent(null);
    setSelectedPosition(null);
    try {
      // Buscar eventos
      const query = new URLSearchParams({ deviceId, from, to });
      const response = await fetchOrThrow(`/api/reports/events?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const eventsData = await response.json();
      setEvents(eventsData);

      if (!eventsData.length) {
        throw Error(t('sharedNoData'));
      }

      // Buscar posições dos eventos
      const positionIds = Array.from(new Set(
        eventsData.map((e) => e.positionId).filter((id) => id)
      ));

      const positionsMap = {};
      if (positionIds.length > 0) {
        // Buscar em batches de 128
        for (let i = 0; i < positionIds.length; i += 128) {
          const batch = positionIds.slice(i, i + 128);
          const posQuery = new URLSearchParams();
          batch.forEach((id) => posQuery.append('id', id));
          const posResponse = await fetchOrThrow(`/api/positions?${posQuery.toString()}`);
          const posArray = await posResponse.json();
          posArray.forEach((p) => { positionsMap[p.id] = p; });
        }
      }
      setPositions(positionsMap);
    } finally {
      setLoading(false);
    }
  });

  // Handler para clique no evento
  const handleEventClick = useCallback((event, position) => {
    setSelectedEvent(event);
    setSelectedPosition(position);
    setShowCard(true);
  }, []);

  // Formatar tipo de evento para exibição
  const formatEventType = (type) => {
    return t(prefixString('event', type));
  };

  return (
    <div className={classes.root}>
      {/* Mapa em tela cheia */}
      <div className={classes.mapContainer}>
        <MapView>
          <MapGeofence />
          {viewMode === 'markers' && filteredEvents.length > 0 && (
            <MapEventMarkers
              events={filteredEvents}
              positions={positions}
              geofences={geofences}
              onClick={handleEventClick}
              showGeofences={showGeofences}
            />
          )}
          {viewMode === 'heatmap' && heatmapPositions.length > 0 && (
            <MapHeatLayer
              positions={heatmapPositions}
              radius={radius}
              intensity={intensity}
              opacity={opacity}
              colorScheme={colorScheme}
              showPoints={false}
            />
          )}
        </MapView>
        <MapScale />
        {mapPositions.length > 0 && <MapCamera positions={mapPositions} />}
      </div>

      {/* Sidebar */}
      <div
        className={classes.sidebar}
        style={{
          backgroundColor: tokens?.glassmorphism?.heavy?.background || 'rgba(13, 17, 23, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
          overflow: 'hidden',
        }}
      >
        <Paper elevation={0} square sx={{ backgroundColor: 'transparent' }}>
          <Toolbar>
            <IconButton edge="start" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" className={classes.title}>
              {t('mapEventTitle')}
            </Typography>
            {loaded && (
              <IconButton edge="end" onClick={() => setSettingsExpanded(!settingsExpanded)}>
                <TuneIcon />
              </IconButton>
            )}
          </Toolbar>
        </Paper>

        <Paper className={classes.content} square elevation={0} sx={{ backgroundColor: 'transparent', borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}` }}>
          {loaded ? (
            <>
              {/* Dispositivo e período */}
              <Box className={classes.section}>
                <Typography variant="subtitle1" align="center" gutterBottom>
                  {deviceName}
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" display="block">
                  {formatTime(from, 'minutes')} - {formatTime(to, 'minutes')}
                </Typography>
              </Box>

              <Divider />

              {/* Toggle Marcadores/Heatmap */}
              <Box className={classes.viewModeToggle}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, val) => val && setViewMode(val)}
                  size="small"
                >
                  <ToggleButton value="markers" sx={{ px: 2 }}>
                    <Tooltip title={t('mapEventMarkers')}>
                      <RoomIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="heatmap" sx={{ px: 2 }}>
                    <Tooltip title={t('reportHeatmap')}>
                      <WhatshotIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Configurações */}
              <Box className={classes.section}>
                <Box
                  className={classes.sectionHeader}
                  onClick={() => setSettingsExpanded(!settingsExpanded)}
                >
                  <Typography variant="subtitle2">
                    {viewMode === 'markers' ? t('sharedType') : t('reportHeatmapSettings')}
                  </Typography>
                  {settingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                <Collapse in={settingsExpanded}>
                  {/* Filtro por tipo - sempre visível */}
                  <Box className={classes.filterButtons}>
                    <ToggleButtonGroup
                      value={filterType}
                      exclusive
                      onChange={(e, val) => val && setFilterType(val)}
                      size="small"
                    >
                      {EVENT_FILTERS.map((filter) => (
                        <ToggleButton
                          key={filter.type}
                          value={filter.type}
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            '&.Mui-selected': filter.color ? {
                              bgcolor: `${filter.color}20`,
                              borderColor: filter.color,
                            } : {},
                          }}
                        >
                          <Tooltip title={filter.label}>
                            {filter.icon}
                          </Tooltip>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  {/* Controles específicos do modo */}
                  {viewMode === 'markers' ? (
                    <>
                      {/* Opções de marcadores */}
                      <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showGeofences}
                              onChange={(e) => setShowGeofences(e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="caption" color="text.secondary">
                              {t('sharedShowGeofences')}
                            </Typography>
                          }
                        />
                      </Box>

                      {/* Legenda */}
                      <Box className={classes.legend} sx={{ mt: 1 }}>
                        {Object.entries(EVENT_ICONS).slice(0, 6).map(([type, config]) => (
                          <Box key={type} className={classes.legendItem}>
                            <span
                              className={classes.legendDot}
                              style={{ backgroundColor: config.color }}
                            />
                            <span>{config.label}</span>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Controles de heatmap */}
                      <Box className={classes.controlsRow} sx={{ mt: 1 }}>
                        <ToggleButtonGroup
                          value={colorScheme}
                          exclusive
                          onChange={(e, val) => val && setColorScheme(val)}
                          size="small"
                        >
                          {HEATMAP_COLOR_SCHEMES.map((scheme) => (
                            <ToggleButton
                              key={scheme}
                              value={scheme}
                              sx={{
                                px: 0.75,
                                py: 0.5,
                                '&.Mui-selected': {
                                  bgcolor: `${COLOR_SCHEME_CONFIG[scheme]?.color}30`,
                                  borderColor: COLOR_SCHEME_CONFIG[scheme]?.color,
                                },
                              }}
                            >
                              <Tooltip title={COLOR_SCHEME_CONFIG[scheme]?.label}>
                                {COLOR_SCHEME_CONFIG[scheme]?.icon}
                              </Tooltip>
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>

                      {/* Raio */}
                      <Box className={classes.sliderRow}>
                        <span className={classes.sliderLabel}>
                          <BlurOnIcon sx={{ fontSize: 14 }} />
                          {t('reportHeatmapRadius')}
                        </span>
                        <Slider
                          value={radius}
                          onChange={(e, val) => setRadius(val)}
                          min={5}
                          max={60}
                          step={5}
                          size="small"
                        />
                        <span className={classes.sliderValue}>{radius}px</span>
                      </Box>

                      {/* Intensidade */}
                      <Box className={classes.sliderRow}>
                        <span className={classes.sliderLabel}>
                          <WhatshotIcon sx={{ fontSize: 14 }} />
                          {t('reportHeatmapIntensity')}
                        </span>
                        <Slider
                          value={intensity}
                          onChange={(e, val) => setIntensity(val)}
                          min={0.1}
                          max={3}
                          step={0.1}
                          size="small"
                        />
                        <span className={classes.sliderValue}>{intensity.toFixed(1)}</span>
                      </Box>

                      {/* Opacidade */}
                      <Box className={classes.sliderRow}>
                        <span className={classes.sliderLabel}>
                          <GradientIcon sx={{ fontSize: 14 }} />
                          {t('reportHeatmapOpacity')}
                        </span>
                        <Slider
                          value={opacity}
                          onChange={(e, val) => setOpacity(val)}
                          min={0.1}
                          max={1}
                          step={0.1}
                          size="small"
                        />
                        <span className={classes.sliderValue}>{Math.round(opacity * 100)}%</span>
                      </Box>
                    </>
                  )}
                </Collapse>
              </Box>

              <Divider />

              {/* Estatísticas */}
              {stats && (
                <Box className={classes.stats} sx={{ mt: 1 }}>
                  <Chip
                    icon={<NotificationsActiveIcon />}
                    label={`${filteredEvents.length}/${stats.total}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {stats.alarms > 0 && (
                    <Tooltip title={t('sharedAlarms')}>
                      <Chip
                        icon={<WarningAmberIcon />}
                        label={stats.alarms}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  {(stats.geofenceEnter + stats.geofenceExit) > 0 && (
                    <Tooltip title={t('sharedGeofences')}>
                      <Chip
                        icon={<PlaceIcon />}
                        label={stats.geofenceEnter + stats.geofenceExit}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  {stats.overspeed > 0 && (
                    <Tooltip title={t('eventDeviceOverspeed')}>
                      <Chip
                        icon={<SpeedIcon />}
                        label={stats.overspeed}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Box>
              )}
            </>
          ) : (
            <ReportFilter onShow={handleShow} deviceType="single" loading={loading} compact />
          )}
        </Paper>
      </div>

      {/* Status Card com informação do evento */}
      {showCard && selectedPosition && (
        <StatusCard
          deviceId={selectedDeviceId}
          position={selectedPosition}
          event={selectedEvent}
          onClose={() => setShowCard(false)}
          disableActions
        />
      )}
    </div>
  );
};

export default EventMapPage;
